// Admin-Panel › Brücke: reine Hilfsfunktionen (Einladungslink für den Bot, Fortschritt, Texte).
import type { BridgeChannelMode, BridgeGuildDto, BridgeGuildStatus } from '@lagerfeuer/shared';

/** Benötigte Bot-Rechte auf der Gegenseite (Bitwerte des Discord-Rechtesystems). */
export const BRIDGE_BOT_PERMISSION_BITS = {
  ADD_REACTIONS: 1n << 6n,
  VIEW_CHANNEL: 1n << 10n,
  SEND_MESSAGES: 1n << 11n,
  EMBED_LINKS: 1n << 14n,
  ATTACH_FILES: 1n << 15n,
  READ_MESSAGE_HISTORY: 1n << 16n,
  MANAGE_WEBHOOKS: 1n << 29n,
  SEND_MESSAGES_IN_THREADS: 1n << 38n,
} as const;

/** Summe der Bits als Dezimal-String (BigInt, da Bit 38 > 2^31). */
export function bridgeBotPermissions(): string {
  return Object.values(BRIDGE_BOT_PERMISSION_BITS)
    .reduce((acc, bit) => acc | bit, 0n)
    .toString();
}

/** Einladungs-URL für den Brücken-Bot; ohne Bot-ID → null. */
export function bridgeInviteUrl(botUserId: string | null | undefined): string | null {
  if (!botUserId || !/^\d+$/.test(botUserId)) return null;
  const params = new URLSearchParams({ client_id: botUserId, scope: 'bot', permissions: bridgeBotPermissions() });
  return `https://discord.com/oauth2/authorize?${params.toString()}`;
}

export const GUILD_STATUS_LABELS: Record<BridgeGuildStatus, string> = {
  PENDING: 'Wartet',
  IMPORTING: 'Importiert',
  ACTIVE: 'Aktiv',
  PAUSED: 'Pausiert',
  ERROR: 'Fehler',
};

export const GUILD_STATUS_TONES: Record<BridgeGuildStatus, 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'> = {
  PENDING: 'neutral',
  IMPORTING: 'accent',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  ERROR: 'danger',
};

export const CHANNEL_MODE_LABELS: Record<BridgeChannelMode, string> = {
  READONLY: 'Nur lesen',
  BIDIRECTIONAL: 'In beide Richtungen',
  PAUSED: 'Pausiert',
};

export const CHANNEL_MODE_HELP: Record<BridgeChannelMode, string> = {
  READONLY: 'Nachrichten von Discord erscheinen hier; Plattform-Nutzer können in diesem Kanal nicht schreiben.',
  BIDIRECTIONAL: 'Nachrichten werden in beide Richtungen gespiegelt (Plattform-Beiträge erscheinen per Webhook mit Name und Avatar).',
  PAUSED: 'Keine Spiegelung in irgendeine Richtung; vorhandene Nachrichten bleiben erhalten.',
};

export const HISTORY_PRESETS = [
  { value: 0, label: 'Alles' },
  { value: 1000, label: '1.000' },
  { value: 10000, label: '10.000' },
] as const;

/** Läuft für eine der Guilds noch ein Import? (steuert das 3-s-Neuladen) */
export function isBridgeBusy(guilds: readonly Pick<BridgeGuildDto, 'status'>[] | undefined): boolean {
  return !!guilds?.some((g) => g.status === 'PENDING' || g.status === 'IMPORTING');
}

export function progressPercent(p: { done: number; total: number } | null | undefined): number | null {
  if (!p || p.total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((p.done / p.total) * 100)));
}

export const IMPORT_PHASE_LABELS: Record<string, string> = {
  structure: 'Struktur',
  members: 'Mitglieder',
  emojis: 'Emojis',
  history: 'Verlauf',
  backfill: 'Verlauf',
  done: 'Fertig',
};
